import { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { StarRounded as Star } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ItemsSearchInput from './ItemsSearchInput';
import ReuseableTable from '../table/ReuseableTable';
import {
  getTextForCategory,
  updateItemInPaginatedList,
} from '../../helpers/items';
import {
  formatDollarAmount,
  roundToMaxDecimalPlaces,
} from '../../utils/global/helper';
import TableDropdownMenu from '../global/TableDropdownMenu';
import useConfirm from '../../hooks/useConfirm';
import useUser from '../../hooks/global/useUser';
import { ItemWithCommissions } from '../../types/items';
import { PERCENT_MAX_TAX_DECIMAL_PLACES } from '../../types/businesses';
import useSearchItems from '../../hooks/items/useSearchItems';
import useUpdateItem from '../../hooks/items/useUpdateItem';
import { invalidateSearchItems } from '../../services/items';
import useDeleteItem from '../../hooks/items/useDeleteItem';
import Error from '../global/Error';
import OutlinedPageBox from '../global/OutlinedPageBox';
import CenteredContentLayout from '../global/CenteredContentLayout';
import PageLoading from '../global/PageLoading';
import FullPageModal from '../modal/FullPageModal';
import { trimString } from '../../helpers/global';
import ItemForm from './ItemForm';
import ItemSideBarModal from './ItemSideBarModal';
import { Optional } from '../../types/global';
import useInfiniteQueryPage from '../../hooks/global/useInfiniteQueryPage';

function ItemsTab() {
  const confirm = useConfirm();
  const theme = useTheme();
  const { user } = useUser();

  const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState<Optional<
    ItemWithCommissions,
    'id'
  > | null>(null);
  const [selectedViewItemId, setSelectedViewItemId] = useState<string | null>(
    null,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const isSearchActive = !!searchQuery.trim();

  const {
    queryKey,
    items,
    totalCount,
    isLoading,
    isError,
    isFetching,
    isDebouncing,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useSearchItems(searchQuery, {
    orderByFavoritedAt: true,
    includeGiftCards: false,
  });

  const { mutate: updateItem, isPending: isUpdating } = useUpdateItem({
    onSuccess: (updatedItem, { payload }) => {
      const isFavoriteChanged = 'favorite' in payload;
      if (isFavoriteChanged) {
        invalidateSearchItems();
      }

      updateItemInPaginatedList({
        queryKey,
        itemId: updatedItem.id,
        data: updatedItem,
      });
    },
    onError: err => {
      toast.error(err);
    },
  });
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteItem({
    onSuccess: () => {
      refetch();
    },
    onError: err => {
      toast.error(err);
    },
  });

  const selectedViewItem = selectedViewItemId
    ? items.find(i => i.id === selectedViewItemId)
    : null;
  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;
  const productTaxPercent = roundToMaxDecimalPlaces(
    productTaxRate * 100,
    PERCENT_MAX_TAX_DECIMAL_PLACES,
  );

  const onNewItem = () => {
    setIsNewItemModalOpen(true);
  };

  const {
    page,
    onPageChange,
    resetPage,
    rowsPerPage,
    displayedItems,
    shouldShowEmptyState,
    shouldShowPagination,
  } = useInfiniteQueryPage({
    data: items,
    hasNextPage,
    fetchNextPage,
    isSearchActive,
    isFetching: isFetching || isDebouncing,
  });

  const onItemClick = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setSelectedViewItemId(itemId);
  };

  const onChangeQuery = (newQuery: string) => {
    setSearchQuery(newQuery);
    if (page > 0) {
      resetPage();
    }
  };

  const error = isError ? 'Error loading items' : '';

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          paddingX: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  return (
    <>
      {!isLoading && (
        <>
          {(!!items.length || isSearchActive || isFetching || isDebouncing) && (
            <>
              <Box
                sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <ItemsSearchInput
                  value={searchQuery}
                  onChange={onChangeQuery}
                  isDebouncing={isDebouncing}
                  disabled={isUpdating || isDeleting}
                />
                <div className="header-right">
                  <Button
                    variant="contained"
                    className="primary"
                    onClick={onNewItem}>
                    New Item
                  </Button>
                </div>
              </Box>

              {displayedItems.length > 0 && (
                <ReuseableTable
                  name="Items"
                  handleRowClick={onItemClick}
                  rows={displayedItems.map(item => ({
                    rowId: item.id,
                    cells: [
                      <Typography key={`${item.id}-name`} variant="inherit">
                        {item.favoritedAt && (
                          <Star
                            sx={{
                              display: 'inline-block',
                              fontSize: theme.typography.body1.fontSize,
                              // eslint-disable-next-line no-restricted-syntax -- typography-exception: favourite-star yellow is a decorative icon colour with no palette token
                              color: '#FDCC0D',
                              verticalAlign: 'text-bottom',
                              mr: 0.5,
                            }}
                          />
                        )}
                        {item.name}
                      </Typography>,
                      getTextForCategory(item.category),
                      item.isTaxable
                        ? `${productTaxPercent}%`
                        : 'Non-taxable item',
                      formatDollarAmount(item.price),
                      <TableDropdownMenu
                        disabled={isUpdating || isFetching || isDeleting}
                        options={[
                          {
                            id: 'edit',
                            label: 'Edit',
                            action: () => {
                              setSelectedEditItem(item);
                            },
                          },
                          {
                            id: 'duplicate',
                            label: 'Duplicate',
                            action: () => {
                              setSelectedEditItem({
                                ...item,
                                id: undefined,
                              });
                            },
                          },
                          {
                            id: 'taxable',
                            label: item.isTaxable
                              ? 'Make non-taxable'
                              : 'Make taxable',
                            action: async () => {
                              updateItem({
                                itemId: item.id,
                                payload: {
                                  isTaxable: !item.isTaxable,
                                },
                              });
                            },
                          },
                          {
                            id: 'favorite',
                            label: item.favoritedAt ? 'Unfavorite' : 'Favorite',
                            action: async () => {
                              updateItem({
                                itemId: item.id,
                                payload: {
                                  favorite: !item.favoritedAt,
                                },
                              });
                            },
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            action: async () => {
                              const confirmed = await confirm({
                                title: 'Delete Item',
                                message: `Are you sure you want to delete ${item.name}?`,
                                negativeText: 'Cancel',
                                positiveText: 'Delete',
                              });
                              if (!confirmed) return;
                              deleteItem({ itemId: item.id });
                            },
                          },
                        ]}
                      />,
                    ],
                  }))}
                  headerCells={[
                    {
                      id: 'name',
                      label: 'Name',
                    },
                    {
                      id: 'category',
                      label: 'Category',
                    },
                    {
                      id: 'isTaxable',
                      label: 'Sales Tax',
                    },
                    {
                      id: 'price',
                      label: 'Default Price',
                    },
                    {
                      id: 'action',
                      label: '',
                    },
                  ]}
                  emptyMessage=""
                  loading={false}
                  pagination={
                    shouldShowPagination
                      ? {
                          colSpan: 5,
                          count: totalCount,
                          rowsPerPage,
                          page,
                          setPage: onPageChange,
                          hasNext:
                            (page + 1) * rowsPerPage < items.length ||
                            hasNextPage,
                          hasPrevious: page > 0,
                        }
                      : undefined
                  }
                />
              )}

              {isSearchActive && items.length === 0 && !isLoading && (
                <OutlinedPageBox>
                  <CenteredContentLayout
                    image={{
                      src: '/price-tag.svg',
                      alt: 'No items found',
                    }}
                    title={`No items found matching "${searchQuery}"`}
                    description="Try a different search term or clear your search to see all items."
                    button={{
                      text: 'Clear Search',
                      onClick: () => setSearchQuery(''),
                    }}
                    maxWidth="750px"
                    imageMaxWidth="120px"
                  />
                </OutlinedPageBox>
              )}
            </>
          )}

          {shouldShowEmptyState && (
            <OutlinedPageBox>
              <CenteredContentLayout
                image={{
                  src: '/price-tag.svg',
                  alt: 'Add an item',
                }}
                title="Add an item to start selling products"
                description="Add products to your item library to help speed up checkout and save your products pricing information."
                button={{
                  text: 'Create an Item',
                  onClick: onNewItem,
                }}
                maxWidth="750px"
                imageMaxWidth="120px"
              />
            </OutlinedPageBox>
          )}
        </>
      )}
      {isLoading && <PageLoading />}
      <FullPageModal
        modalOpen={isNewItemModalOpen || !!selectedEditItem}
        name={
          isNewItemModalOpen || !!selectedEditItem
            ? selectedEditItem?.id
              ? trimString(selectedEditItem.name, 20)
              : 'Create an Item'
            : ''
        }
        setModalOpen={() => {
          setIsNewItemModalOpen(false);
          setSelectedEditItem(null);
        }}>
        {(isNewItemModalOpen || !!selectedEditItem) && (
          <ItemForm
            item={selectedEditItem}
            onSuccess={(_, type) => {
              setIsNewItemModalOpen(false);
              setSelectedEditItem(null);
              const isNew = type === 'create';
              if (isNew) {
                resetPage();
              }
            }}
          />
        )}
      </FullPageModal>
      <ItemSideBarModal
        isOpen={!!selectedViewItem}
        item={selectedViewItem}
        onClose={() => setSelectedViewItemId(null)}
      />
    </>
  );
}

export default ItemsTab;
